// Quality Signals — Constellations explorer. Server-side semantic/fuzzy
// clustering of kb_signals along a chosen dimension (gurucloud-kb kb.cluster()).
// Bubbles are sized by membership and COLORED by heat (how recent the
// cluster's signals are, by event date); pick a fixed window preset (7 / 14 /
// 30 days — precomputed, so they load instantly) or a custom range; click a
// bubble to list its signals, then click a signal to open it full-screen in a
// modal you can page through with Prev/Next.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  DEFAULT_HEAT_RULES,
  HEAT_COLORS,
  bubbleTone,
  describeHeat,
  heatLegend,
  type BubbleTone,
} from '../utils/constellationHeat';
import { nextRefreshPollDelay } from '../utils/refreshPoll';
import { pctToWindowISO, pctToWindowLabel } from '../utils/trailingWindow';
import { SignalsTabs } from '../components/signals/SignalsTabs';
import { PortalSignalsTabs } from '../components/signals/PortalSignalsTabs';
import {
  signalsClustersApi,
  WINDOW_PRESET_KEYS,
  type Cluster,
  type ClustersParams,
  type ClustersResponse,
  type ConstellationDimension,
  type SignalDetail,
  type WindowPreset,
} from '../services/signalsClusters';
import styles from './SignalsConstellationsPage.module.css';

function describeLoadError(err: unknown, what: string): string {
  const status = (err as { response?: { status?: number; data?: { detail?: string } } })?.response?.status;
  const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
  if (status === 401 || status === 403) {
    return `You need admin access to view ${what}.`;
  }
  if (status != null) {
    return `Could not load ${what} — the server returned HTTP ${status}${detail ? `: ${detail}` : ''}.`;
  }
  return `Could not reach the server to load ${what}.`;
}

const WINDOW_SPAN_DAYS = 180;

// Window picker: the fixed presets are the primary control (PJ, 2026-09-08:
// "usually easier to just have like 7 day, 14 day"); the slider stays as a
// secondary "Custom range" mode. Preset bounds are resolved by the backend so
// they hit the views the beat warmer keeps precomputed.
type WindowMode = 'all' | WindowPreset | 'custom';
const PRESET_LABEL: Record<WindowPreset, string> = {
  '7d': 'Last 7 days',
  '14d': 'Last 14 days',
  '30d': 'Last 30 days',
};

// Known metadata bridge keys surfaced in the modal (label, in display order).
const META_KEYS: ReadonlyArray<readonly [string, string]> = [
  ['customer_no', 'Customer'], ['account_id', 'Account'], ['order_id', 'Order'],
  ['sage_so_no', 'Sage SO'], ['sku', 'SKU'], ['batch_id', 'Batch'],
  ['signal_type', 'Type'], ['status', 'Status'],
];

function metaPairs(meta: Record<string, unknown>): Array<[string, string]> {
  return META_KEYS.filter(([k]) => {
    const v = meta[k];
    return v != null && String(v).trim() !== '';
  }).map(([k, label]) => [label, String(meta[k])]);
}

// Event time (metadata.observed_at) is the source's own date — distinct from
// ingest time. fmtDay renders it for the pulse line and the signal modal.
function fmtDay(iso: string | undefined | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

// Relative age of the served clustering ("updated 12m ago") — the cache is
// stale-while-revalidate, so the stats line tells the reader how old the view
// is and whether a background refresh is already running.
function relAge(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

// Hot bubbles shade by burst share (recent ÷ dated members): a cluster that
// is ALL recent activity reads darker than one with a couple of fresh
// signals on an old topic — on a mature bank most topics recur, so "hot"
// alone separates little; the shade is what distinguishes a flare-up.
export function hotShade(burstScore: number): number {
  const s = Math.min(1, Math.max(0, burstScore));
  return Math.round((0.14 + 0.36 * s) * 100) / 100;
}

interface Bubble {
  cluster: Cluster;
  x: number;
  y: number;
  r: number;
  tone: BubbleTone;
  color: string;
}

// Deterministic collision-free circle packing: largest first at the center,
// each next bubble spiral-searched to the first non-overlapping spot, then the
// whole layout normalized into the 0..100 viewBox with padding. Replaces the
// golden-angle spiral, whose bubbles overlapped into an unreadable blob at
// 60+ clusters.
const LABEL_MIN_R = 5; // smaller bubbles show their label on hover only
function packBubbles(clusters: Cluster[], maxSize: number): Bubble[] {
  if (clusters.length === 0) return [];
  const sorted = [...clusters]
    .map((cluster) => {
      const tone = bubbleTone(cluster);
      return {
        cluster,
        r: 4 + 11 * Math.sqrt(cluster.size / maxSize),
        tone,
        color: HEAT_COLORS[tone],
      };
    })
    .sort((a, b) => b.r - a.r);
  const placed: Array<{ x: number; y: number; r: number }> = [];
  const out: Bubble[] = [];
  const PAD = 1.6; // breathing room between circles (label space)
  for (const b of sorted) {
    let x = 0;
    let y = 0;
    if (placed.length > 0) {
      let t = 0;
      for (;;) {
        t += 0.35;
        const rad = 0.55 * t;
        x = rad * Math.cos(t);
        y = rad * Math.sin(t);
        const collides = placed.some(
          (p) => Math.hypot(p.x - x, p.y - y) < p.r + b.r + PAD,
        );
        if (!collides) break;
      }
    }
    placed.push({ x, y, r: b.r });
    out.push({ cluster: b.cluster, x, y, r: b.r, tone: b.tone, color: b.color });
  }
  const minX = Math.min(...out.map((b) => b.x - b.r));
  const maxX = Math.max(...out.map((b) => b.x + b.r));
  const minY = Math.min(...out.map((b) => b.y - b.r));
  const maxY = Math.max(...out.map((b) => b.y + b.r));
  const span = Math.max(maxX - minX, maxY - minY, 1);
  const scale = 92 / span;
  const ox = (100 - (maxX - minX) * scale) / 2;
  const oy = (100 - (maxY - minY) * scale) / 2;
  return out.map((b) => ({
    ...b,
    x: (b.x - minX) * scale + ox,
    y: (b.y - minY) * scale + oy,
    r: b.r * scale,
  }));
}

// pct 0..100 across a trailing WINDOW_SPAN_DAYS window (100 = now, 0 = oldest).
// API bounds are day-quantized (see utils/trailingWindow) so repeated visits —
// even hours apart — reuse the backend's clustering cache instead of forcing
// a recompute.
const pctToLabel = (pct: number): string => pctToWindowLabel(pct, WINDOW_SPAN_DAYS);

// While the backend reports refreshing=true (it served a stale view and
// scheduled a background recompute), re-fetch a few times so the fresh view
// lands on screen without a manual reload. Bounded: the poll gives up after
// MAX_REFRESH_POLLS in case the refresh keeps failing upstream. A
// computing=true response (async first-touch: the view has never been
// computed and the placeholder is empty) gets a larger budget — the first
// compute is the whole page content, not just a freshness upgrade.
const REFRESH_POLL_MS = 5_000;
const MAX_REFRESH_POLLS = 6;
const COMPUTING_MAX_POLLS = 12;

export function SignalsConstellationsPage({
  variant = 'admin',
}: { variant?: 'admin' | 'portal' } = {}) {
  // Portal (reader) variant: portal sub-nav + a prominent beta notice, and
  // exec-friendly copy (no internal table names).
  const isPortal = variant === 'portal';
  const [resp, setResp] = useState<ClustersResponse | null>(null);
  const [dimensions, setDimensions] = useState<ConstellationDimension[]>([]);
  const [dimension, setDimension] = useState('observation');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // drill-down modal: index of the member within the selected cluster (null = closed)
  const [modalIndex, setModalIndex] = useState<number | null>(null);
  const [detail, setDetail] = useState<SignalDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);

  // bubble-field text filter (label + keywords)
  const [filter, setFilter] = useState('');
  // zoom/pan: the svg viewBox (square). w=100 is the fitted view.
  const [vb, setVb] = useState({ x: 0, y: 0, w: 100 });
  const dragRef = useRef<{ px: number; py: number; vx: number; vy: number } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  // time window. `windowMode` picks all-time / a fixed preset / the custom
  // slider. For the slider, r0/r1 track the thumbs LIVE (labels follow the
  // drag); `committed` updates only on release (pointer up / key up), and
  // only the committed window drives fetches — one request per drag GESTURE
  // instead of one per drag step, since every distinct window is its own
  // backend cache key (and, first-touch, its own background compute).
  const [windowMode, setWindowMode] = useState<WindowMode>('all');
  const [r0, setR0] = useState(0); // older bound
  const [r1, setR1] = useState(100); // newer bound
  const [committed, setCommitted] = useState({ r0: 0, r1: 100 });

  // Two loads can race (a slow first-touch response must not clobber the
  // result of a newer request the user has since triggered) — only the
  // latest sequence number may write state.
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    try {
      const params: ClustersParams = { dimension };
      if (windowMode === 'custom') {
        params.window_start = pctToWindowISO(committed.r0, WINDOW_SPAN_DAYS, 'start');
        params.window_end = pctToWindowISO(committed.r1, WINDOW_SPAN_DAYS, 'end');
      } else if (windowMode !== 'all') {
        params.preset = windowMode;
      }
      const data = await signalsClustersApi.list(params);
      if (seq !== loadSeq.current) return; // superseded by a newer request
      setResp(data);
      if (data.dimensions.length) setDimensions(data.dimensions);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setError(describeLoadError(err, 'clusters'));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [dimension, windowMode, committed]);

  useEffect(() => {
    const t = setTimeout(load, 150);
    return () => clearTimeout(t);
  }, [load]);

  // refreshing=true means the backend served a stale view and a background
  // recompute is in flight — poll until the fresh view replaces it (bounded).
  const refreshPolls = useRef(0);
  useEffect(() => {
    refreshPolls.current = 0; // new parameter set -> new poll budget
  }, [dimension, windowMode, committed]);
  useEffect(() => {
    const delay = nextRefreshPollDelay({
      refreshing: resp?.refreshing ?? false,
      loading,
      pollsSoFar: refreshPolls.current,
      maxPolls: resp?.computing ? COMPUTING_MAX_POLLS : MAX_REFRESH_POLLS,
      intervalMs: REFRESH_POLL_MS,
    });
    if (delay === null) return;
    const t = setTimeout(() => {
      refreshPolls.current += 1;
      void load();
    }, delay);
    return () => clearTimeout(t);
  }, [resp, loading, load]);

  const clusters = resp?.clusters ?? [];
  const maxSize = useMemo(
    () => clusters.reduce((m, c) => Math.max(m, c.size), 1),
    [clusters],
  );

  const matchesFilter = useCallback(
    (c: Cluster): boolean => {
      const q = filter.trim().toLowerCase();
      if (!q) return true;
      if (c.label.toLowerCase().includes(q)) return true;
      return c.keywords.some((k) => k.toLowerCase().includes(q));
    },
    [filter],
  );
  const matchCount = useMemo(
    () => (filter.trim() ? clusters.filter(matchesFilter).length : clusters.length),
    [clusters, matchesFilter, filter],
  );

  // Only the clusters that match the filter get laid out — filtering REMOVES
  // the rest and re-packs, instead of dimming them into a busier picture.
  const visibleClusters = useMemo(
    () => clusters.filter(matchesFilter),
    [clusters, matchesFilter],
  );

  const bubbles = useMemo(
    (): Bubble[] => packBubbles(visibleClusters, maxSize),
    [visibleClusters, maxSize],
  );

  // refit the view whenever the layout changes (filter / dimension switch)
  useEffect(() => {
    setVb({ x: 0, y: 0, w: 100 });
  }, [visibleClusters.length, dimension]);

  const zoomBy = useCallback((scale: number, anchor?: { x: number; y: number }) => {
    setVb((cur) => {
      const w = Math.min(160, Math.max(10, cur.w * scale));
      const pt = anchor ?? { x: cur.x + cur.w / 2, y: cur.y + cur.w / 2 };
      const f = w / cur.w;
      return { x: pt.x - (pt.x - cur.x) * f, y: pt.y - (pt.y - cur.y) * f, w };
    });
  }, []);

  // React's onWheel is a passive listener, so the page scrolls WHILE zooming —
  // attach a native non-passive handler and preventDefault so the wheel zooms
  // cleanly over the stage.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      setVb((cur) => {
        const w = Math.min(160, Math.max(10, cur.w * (e.deltaY > 0 ? 1.15 : 1 / 1.15)));
        const pt = {
          x: cur.x + ((e.clientX - r.left) / r.width) * cur.w,
          y: cur.y + ((e.clientY - r.top) / r.height) * cur.w,
        };
        const f = w / cur.w;
        return { x: pt.x - (pt.x - cur.x) * f, y: pt.y - (pt.y - cur.y) * f, w };
      });
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [loading, error, clusters.length]);
  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    dragRef.current = { px: e.clientX, py: e.clientY, vx: vb.x, vy: vb.y };
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const d = dragRef.current;
    const el = svgRef.current;
    if (!d || !el) return;
    const r = el.getBoundingClientRect();
    setVb((cur) => ({
      ...cur,
      x: d.vx - ((e.clientX - d.px) / r.width) * cur.w,
      y: d.vy - ((e.clientY - d.py) / r.height) * cur.w,
    }));
  };
  const endDrag = () => { dragRef.current = null; };
  const resetView = () => setVb({ x: 0, y: 0, w: 100 });
  const zoomed = vb.x !== 0 || vb.y !== 0 || vb.w !== 100;

  const selected = clusters.find((c) => c.cluster_id === selectedId) ?? null;
  const memberCount = selected?.members.length ?? 0;
  const modalMember =
    modalIndex != null && selected ? (selected.members[modalIndex] ?? null) : null;

  const selectCluster = (id: number | null) => {
    setSelectedId(id);
    setModalIndex(null); // a different cluster invalidates the open signal
  };
  const closeModal = useCallback(() => setModalIndex(null), []);
  const goPrev = useCallback(
    () => setModalIndex((i) => (i == null ? i : Math.max(0, i - 1))),
    [],
  );
  const goNext = useCallback(
    () =>
      setModalIndex((i) =>
        i == null ? i : Math.min(memberCount - 1, i + 1),
      ),
    [memberCount],
  );

  // lazily fetch the full signal whenever the open member changes
  useEffect(() => {
    const id = modalMember?.id;
    if (!id) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(false);
    setDetail(null);
    signalsClustersApi
      .getSignal(id)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch(() => { if (!cancelled) setDetailError(true); })
      .finally(() => { if (!cancelled) setDetailLoading(false); });
    return () => { cancelled = true; };
  }, [modalMember?.id]);

  // keyboard nav while the modal is open
  useEffect(() => {
    if (modalIndex == null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modalIndex, closeModal, goNext, goPrev]);

  const onRange = (which: 'r0' | 'r1', raw: number) => {
    if (which === 'r0') setR0(Math.min(raw, r1 - 2));
    else setR1(Math.max(raw, r0 + 2));
  };
  // Fetch only when the drag gesture ends (or a keyboard step is released).
  const commitWindow = () => setCommitted({ r0, r1 });
  const pickWindow = (mode: WindowMode) => {
    setWindowMode(mode);
    selectCluster(null); // a different scope invalidates the open cluster
  };

  // What the served view is actually scoped to, from the payload's own
  // bounds (a preset resolves server-side; the slider echoes its bounds).
  const windowSummary = (() => {
    if (windowMode === 'custom') return `${pctToLabel(r0)} → ${pctToLabel(r1)}`;
    if (windowMode === 'all') return 'All signals';
    const since = fmtDay(resp?.preset === windowMode ? resp?.window_start : null);
    return since ? `${PRESET_LABEL[windowMode]} · since ${since}` : PRESET_LABEL[windowMode];
  })();

  const heatRules = resp?.heat_rules ?? DEFAULT_HEAT_RULES;
  const legendRows = heatLegend(heatRules);

  return (
    <div className={styles.page}>
      {isPortal ? <PortalSignalsTabs /> : <SignalsTabs />}

      {isPortal && (
        <div className={styles.betaNotice} role="note">
          <strong>Beta</strong> — this explorer is experimental. It groups related
          quality signals automatically; the clusters and their labels may shift
          as we refine it.
        </div>
      )}

      <header className={styles.head}>
        <h1>
          Constellations{' '}
          <span className={styles.beta}>{isPortal ? 'beta' : 'new'}</span>
        </h1>
        <p>
          {isPortal
            ? 'Semantic clusters of your quality signals, named automatically. Pick a dimension and a time window, and click a bubble to read the underlying signals. Bubble color shows how recent the cluster is.'
            : 'Semantic clusters of kb_signals, named automatically. Pick a dimension and a time window, and click a bubble to read its signals. Bubble color shows how recent the cluster is.'}
        </p>
      </header>

      <div className={styles.dimBar}>
        <span className={styles.dimLabel}>Cluster along</span>
        <div className={styles.dims}>
          {(dimensions.length ? dimensions : [{ key: 'observation', label: 'Observation', kind: 'semantic' as const }]).map((d) => (
            <button
              key={d.key}
              type="button"
              className={`${dimension === d.key ? styles.on : ''} ${d.kind === 'facet' ? styles.facet : ''}`}
              onClick={() => { setDimension(d.key); selectCluster(null); }}
              title={d.kind === 'facet' ? 'Metadata facet — fuzzy match' : 'Semantic dimension — vector clustering'}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.windowBar}>
        <div className={styles.windowHead}>
          <span
            className={styles.dimLabel}
            title="Scopes by INGEST date (when the signal entered the bank) — the platform can't range-filter by event date yet. Bubble heat always reflects event dates."
          >
            Window
          </span>
          <div className={styles.presets} role="group" aria-label="Time window">
            <button
              type="button"
              className={windowMode === 'all' ? styles.on : ''}
              aria-pressed={windowMode === 'all'}
              onClick={() => pickWindow('all')}
            >
              All time
            </button>
            {WINDOW_PRESET_KEYS.map((key) => (
              <button
                key={key}
                type="button"
                className={windowMode === key ? styles.on : ''}
                aria-pressed={windowMode === key}
                title="Precomputed — loads instantly"
                onClick={() => pickWindow(key)}
              >
                {PRESET_LABEL[key]}
              </button>
            ))}
            <button
              type="button"
              className={`${styles.custom} ${windowMode === 'custom' ? styles.on : ''}`}
              aria-pressed={windowMode === 'custom'}
              title="Pick any range on a slider (computed on first use)"
              onClick={() => pickWindow('custom')}
            >
              Custom range…
            </button>
          </div>
          <span className={styles.windowRange}>{windowSummary}</span>
        </div>
        {windowMode === 'custom' && (
          <div className={styles.rangeWrap} data-testid="custom-range">
            <div className={styles.track} />
            <div
              className={styles.trackFill}
              style={{ left: `${r0}%`, width: `${r1 - r0}%` }}
            />
            <input
              type="range" min={0} max={100} value={r0}
              onChange={(e) => onRange('r0', Number(e.target.value))}
              onPointerUp={commitWindow}
              onKeyUp={commitWindow}
              aria-label="Window start"
            />
            <input
              type="range" min={0} max={100} value={r1}
              onChange={(e) => onRange('r1', Number(e.target.value))}
              onPointerUp={commitWindow}
              onKeyUp={commitWindow}
              aria-label="Window end"
            />
          </div>
        )}
      </div>

      {resp && (
        <div className={styles.statsLine}>
          <b>{resp.entry_count}</b> signals · <b>{resp.cluster_count}</b> clusters
          {' · '}<b>{resp.clustered_count}</b> grouped, <b>{resp.noise_count}</b> outliers
          {resp.silhouette != null && <> · cohesion {resp.silhouette.toFixed(2)}</>}
          {' · '}<span className={styles.method}>{resp.method}{resp.algorithm ? ` · ${resp.algorithm}` : ''}</span>
          {resp.computed_at && (
            <span
              className={styles.freshness}
              title="Clusterings are precomputed and cached. A stale view is served instantly while a fresh one is computed in the background."
            >
              {' · '}updated {relAge(resp.computed_at)}
              {resp.refreshing && <em> · refreshing…</em>}
            </span>
          )}
        </div>
      )}

      {loading && <div className={styles.muted}>Clustering…</div>}
      {error && <div className={styles.error}>{error}</div>}
      {!loading && !error && resp?.computing && (
        <div className={styles.muted}>
          Clustering this view for the first time — it runs in the background
          and appears here in a few seconds…
        </div>
      )}
      {!loading && !error && clusters.length > 0 && bubbles.length === 0 && (
        <div className={styles.muted}>No clusters match “{filter}”.</div>
      )}
      {!loading && !error && !resp?.computing && clusters.length === 0 && (
        <div className={styles.muted}>
          No clusters in this view yet — they appear as signals accumulate in kb_signals
          (try a wider window or a different dimension).
        </div>
      )}

      {!loading && !error && clusters.length > 0 && (
        <div className={styles.split}>
          <div className={styles.stage}>
            <div
              className={styles.legend}
              role="note"
              aria-label="Bubble color legend"
              title="Heat comes from each signal's own event date (when it happened), not when it was ingested. Size is the number of signals."
            >
              <span className={styles.legendTitle}>Color = how recent</span>
              {legendRows.map((row) => (
                <span key={row.tone} className={styles.legendItem}>
                  <span className={styles.heatDot} style={{ background: HEAT_COLORS[row.tone] }} />
                  <b>{row.label}</b>
                  <span className={styles.legendMeaning}> — {row.meaning}</span>
                </span>
              ))}
              <span className={styles.legendItem}>
                <span className={`${styles.heatDot} ${styles.heatDotDashed}`} style={{ borderColor: HEAT_COLORS.outliers }} />
                <b>Outliers</b>
                <span className={styles.legendMeaning}> — signals that fit no cluster</span>
              </span>
              <span className={styles.legendNote}>by event date, not ingest date · size = signals · darker hot = larger share of recent signals</span>
            </div>
            <div className={styles.stageBar}>
              <input
                className={styles.filterBox}
                placeholder="Filter clusters — label or keyword…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              />
              {filter.trim() && (
                <span className={styles.filterCount}>
                  showing {matchCount} of {clusters.length}
                </span>
              )}
              <span className={styles.zoomHint}>scroll to zoom · drag to pan</span>
              <span className={styles.zoomBtns}>
                <button type="button" className={styles.resetBtn} onClick={() => zoomBy(1 / 1.4)} aria-label="Zoom in">+</button>
                <button type="button" className={styles.resetBtn} onClick={() => zoomBy(1.4)} aria-label="Zoom out">−</button>
                {zoomed && (
                  <button type="button" className={styles.resetBtn} onClick={resetView}>
                    Reset
                  </button>
                )}
              </span>
            </div>
            <svg
              ref={svgRef}
              viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.w}`}
              preserveAspectRatio="xMidYMid meet"
              className={styles.svg}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerLeave={endDrag}
              onDoubleClick={resetView}
              style={{ cursor: dragRef.current ? 'grabbing' : 'grab', touchAction: 'none' }}
            >
              {bubbles.map((b) => {
                const isSel = b.cluster.cluster_id === selectedId;
                const dimmed = selectedId != null && !isSel;
                const heat = b.cluster.temporal?.heat ?? null;
                const cold = heat === 'cold';
                const noise = b.tone === 'outliers';
                const emergent = !noise && b.cluster.from_noise === true;
                const loose = !noise && b.cluster.low_cohesion === true;
                const flags = [
                  emergent ? 'emergent group (regrouped outliers)' : null,
                  loose ? 'loose grouping' : null,
                ].filter(Boolean).join(' · ');
                const heatText = noise
                  ? 'Outliers — signals that fit no cluster'
                  : describeHeat(b.cluster.temporal, heatRules);
                return (
                  <g
                    key={b.cluster.cluster_id}
                    className={styles.bubble}
                    data-tone={b.tone}
                    style={{ opacity: dimmed ? 0.3 : cold ? 0.6 : 1 }}
                    onClick={() => selectCluster(b.cluster.cluster_id)}
                  >
                    <title>{`${b.cluster.label} — ${b.cluster.size} signal${b.cluster.size === 1 ? '' : 's'}\n${heatText}${flags ? `\n${flags}` : ''}`}</title>
                    {heat === 'hot' && !noise && (
                      <circle
                        className={styles.hotPulse}
                        cx={b.x} cy={b.y} r={b.r + 1.6}
                        fill="none" stroke={b.color} strokeWidth={0.7}
                      />
                    )}
                    <circle cx={b.x} cy={b.y} r={b.r} fill={b.color} fillOpacity={noise ? 0.08 : emergent ? 0.12 : cold ? 0.1 : heat === 'hot' ? hotShade(b.cluster.temporal?.burst_score ?? 0) : 0.18} stroke={b.color} strokeWidth={0.5} strokeDasharray={noise ? '1.6 1' : emergent ? '2.6 1.4' : cold ? '1 1' : undefined} />
                    <circle cx={b.x} cy={b.y} r={Math.max(2, b.r * 0.4)} fill={b.color} fillOpacity={emergent ? 0.75 : 1} />
                    <text x={b.x} y={b.y} textAnchor="middle" dominantBaseline="central" style={{ fontSize: '2.8px', fontWeight: 800, fill: '#fff' }}>
                      {b.cluster.size}
                    </text>
                    {b.r >= LABEL_MIN_R && (
                      <text
                        x={b.x}
                        y={b.y + b.r + 2.6}
                        textAnchor="middle"
                        style={{
                          fontSize: '2.4px',
                          fill: '#1e293b',
                          fontWeight: 600,
                          paintOrder: 'stroke',
                          stroke: '#fff',
                          strokeWidth: '0.8px',
                          strokeLinejoin: 'round',
                        }}
                      >
                        {b.cluster.label.length > 24 ? `${b.cluster.label.slice(0, 23)}…` : b.cluster.label}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>

          <div className={styles.detail}>
            {selected ? (
              <div>
                <div className={styles.dh}>
                  <div className={styles.dt}>{selected.label}</div>
                  <div className={styles.dc}>
                    {selected.size} signal{selected.size === 1 ? '' : 's'}
                    {selected.keywords.length > 0 && ` · ${selected.keywords.slice(0, 4).join(', ')}`}
                  </div>
                  {(selected.is_noise || selected.from_noise || selected.low_cohesion) && (
                    <div className={styles.flagRow}>
                      {selected.is_noise && (
                        <span className={styles.flagChip} title="Signals the clusterer could not place in any group — browsable here so nothing is hidden.">
                          outliers
                        </span>
                      )}
                      {selected.from_noise && !selected.is_noise && (
                        <span className={styles.flagChip} title="Formed by re-grouping outlier signals (outlier_strategy=subcluster) — a real group, but not a dense topic core.">
                          emergent — regrouped outliers
                        </span>
                      )}
                      {selected.low_cohesion && !selected.is_noise && (
                        <span className={`${styles.flagChip} ${styles.flagLoose}`} title="This cluster is much more spread out than its peers — it may be a catch-all mixing several topics.">
                          loose grouping
                        </span>
                      )}
                    </div>
                  )}
                  {!selected.is_noise && (
                    <div className={styles.pulseLine} title={describeHeat(selected.temporal, heatRules)}>
                      <span className={styles.heatDot} style={{ background: HEAT_COLORS[bubbleTone(selected)] }} />
                      <b>{selected.temporal?.heat ?? 'undated'}</b>
                      {selected.temporal ? (
                        <>
                          {' · '}{fmtDay(selected.temporal.first_seen)} → {fmtDay(selected.temporal.last_seen)}
                          {' · '}{selected.temporal.recent_count} of {selected.temporal.dated_count} dated in last {heatRules.hot_days}d
                        </>
                      ) : (
                        <span className={styles.heatNote}> · no event dates on these signals</span>
                      )}
                    </div>
                  )}
                </div>
                <div className={styles.body}>
                  {selected.members.length === 0 && (
                    <div className={styles.muted}>No member previews returned.</div>
                  )}
                  {selected.members.map((m, i) => (
                    <button
                      key={m.id}
                      type="button"
                      className={styles.sigRow}
                      onClick={() => setModalIndex(i)}
                      title="Open the full signal"
                    >
                      <span className={styles.obs}>{m.snippet || m.value}</span>
                      <span className={styles.sigMeta}>
                        <span className={styles.metaChip}>{m.customer_no ?? 'unresolved'}</span>
                        {m.observed_at && <span className={styles.metaDate}>{fmtDay(m.observed_at)}</span>}
                        {m.signal_type && <span className={styles.metaType}>{m.signal_type}</span>}
                        <span className={styles.openHint}>open →</span>
                      </span>
                    </button>
                  ))}
                  {selected.size > selected.members.length && (
                    <div className={styles.moreNote}>
                      Showing the {selected.members.length} most representative of {selected.size}.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className={styles.placeholder}>← Click a bubble to read its signals.</div>
            )}
          </div>
        </div>
      )}

      {modalMember && selected && (
        <div
          className={styles.backdrop}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className={styles.modal} role="dialog" aria-modal="true" aria-label="Signal detail">
            <div className={styles.mHead}>
              <div className={styles.mHeadText}>
                <div className={styles.mCluster}>{selected.label}</div>
                <div className={styles.mTitle} title={`Signal ${modalMember.id}`}>
                  {modalMember.customer_no ?? 'Unresolved customer'}
                  {modalMember.observed_at ? ` · ${fmtDay(modalMember.observed_at)}` : ''}
                </div>
              </div>
              <div className={styles.mCounter}>{(modalIndex ?? 0) + 1} of {memberCount}</div>
              <button type="button" className={styles.mClose} onClick={closeModal} aria-label="Close">×</button>
            </div>

            <div className={styles.mBody}>
              {detailLoading && <div className={styles.muted}>Loading signal…</div>}
              {detailError && (
                <div className={styles.error}>Couldn&apos;t load this signal.</div>
              )}
              {!detailLoading && !detailError && detail && (
                <>
                  <p className={styles.mLead}>
                    {detail.observation.trim() || '(no observation text)'}
                  </p>
                  {fmtDay(String(detail.metadata.observed_at ?? '')) && (
                    <div className={styles.field}>
                      <div className={styles.fLabel}>Observed</div>
                      <div className={styles.fVal}>
                        {fmtDay(String(detail.metadata.observed_at))}
                        <span className={styles.heatNote}> (event date from the source)</span>
                      </div>
                    </div>
                  )}
                  {detail.significance.trim() && (
                    <div className={styles.field}>
                      <div className={styles.fLabel}>Significance</div>
                      <div className={styles.fVal}>{detail.significance}</div>
                    </div>
                  )}
                  {detail.products.length > 0 && (
                    <div className={styles.field}>
                      <div className={styles.fLabel}>Products</div>
                      <div className={styles.fVal}>
                        {detail.products.map((p) => (
                          <span key={p} className={styles.chip}>{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {detail.themes.length > 0 && (
                    <div className={styles.field}>
                      <div className={styles.fLabel}>Themes</div>
                      <div className={styles.fVal}>
                        {detail.themes.map((t) => (
                          <span key={t} className={styles.chip}>{t}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {detail.context.trim() && (
                    <div className={styles.field}>
                      <div className={styles.fLabel}>Context</div>
                      <div className={styles.fVal}>{detail.context}</div>
                    </div>
                  )}
                  {metaPairs(detail.metadata).length > 0 && (
                    <div className={styles.meta}>
                      {metaPairs(detail.metadata).map(([label, value]) => (
                        <span key={label}>
                          <b>{label}:</b> {value}
                        </span>
                      ))}
                    </div>
                  )}
                </>
              )}
              {!detailLoading && !detailError && !detail && (
                <div className={styles.muted}>No detail available for this signal.</div>
              )}
            </div>

            <div className={styles.mFoot}>
              <button
                type="button"
                className={styles.navBtn}
                onClick={goPrev}
                disabled={(modalIndex ?? 0) === 0}
              >
                ← Prev
              </button>
              <span className={styles.mHint}>← → to page · Esc to close</span>
              <button
                type="button"
                className={styles.navBtn}
                onClick={goNext}
                disabled={(modalIndex ?? 0) >= memberCount - 1}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
